import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { ExtractedStoreData } from '@/types/store-extraction';

export type UserType = 'brand' | 'agency' | 'exploring';

export type UseCase =
  | 'convert_customers'
  | 'collect_feedback'
  | 'improve_segmentation'
  | 'product_research'
  | 'stand_out';

export type ESPChoice = 'klaviyo' | 'braze' | 'emarsys' | 'salesforce' | 'other';

export interface OnboardingState {
  // Wizard navigation
  currentStep: number;
  completedSteps: number[];

  // Page 1: Account
  firstName: string;
  lastName: string;
  email: string;
  userType: UserType | null;

  // Page 2: Brand
  brandName: string;
  shopUrl: string;
  storeData: ExtractedStoreData | null;

  // Page 3: Goals
  useCases: UseCase[];

  // Page 4: ESP
  espChoice: ESPChoice | null;
  klaviyoConnected: boolean;

  // Page 5: Preview
  aiGenerationStatus: 'idle' | 'extracting' | 'generating' | 'done' | 'error';
  aiGenerationError: string | null;
  generatedFormId: string | null;

  // Auth state
  isAuthenticated: boolean;
  userId: string | null;
  companyId: string | null;

  // Invited user context
  isInvitedUser: boolean;
  existingKlaviyoConnected: boolean;

  // Actions
  setStep: (step: number) => void;
  completeStep: (step: number) => void;
  setAccountData: (data: { firstName: string; lastName: string; email: string }) => void;
  setUserType: (type: UserType) => void;
  setBrandData: (data: { brandName: string; shopUrl: string }) => void;
  setStoreData: (data: ExtractedStoreData | null) => void;
  setUseCases: (cases: UseCase[]) => void;
  setEspChoice: (esp: ESPChoice) => void;
  setKlaviyoConnected: (connected: boolean) => void;
  setAiGenerationStatus: (status: OnboardingState['aiGenerationStatus']) => void;
  setAiGenerationError: (error: string | null) => void;
  setGeneratedFormId: (id: string | null) => void;
  setAuthData: (data: { userId: string; companyId: string }) => void;
  setInvitedUser: (isInvited: boolean, klaviyoConnected: boolean) => void;
  reset: () => void;
}

const initialState = {
  currentStep: 0,
  completedSteps: [] as number[],
  firstName: '',
  lastName: '',
  email: '',
  userType: null as UserType | null,
  brandName: '',
  shopUrl: '',
  storeData: null as ExtractedStoreData | null,
  useCases: [] as UseCase[],
  espChoice: null as ESPChoice | null,
  klaviyoConnected: false,
  aiGenerationStatus: 'idle' as const,
  aiGenerationError: null as string | null,
  generatedFormId: null as string | null,
  isAuthenticated: false,
  userId: null as string | null,
  companyId: null as string | null,
  isInvitedUser: false,
  existingKlaviyoConnected: false,
};

export const useOnboardingStore = create<OnboardingState>()(
  persist(
    (set) => ({
      ...initialState,
      setStep: (step) => set({ currentStep: step }),
      completeStep: (step) =>
        set((s) => ({
          completedSteps: s.completedSteps.includes(step)
            ? s.completedSteps
            : [...s.completedSteps, step],
        })),
      setAccountData: (data) => set(data),
      setUserType: (type) => set({ userType: type }),
      setBrandData: (data) => set(data),
      setStoreData: (data) => set({ storeData: data }),
      setUseCases: (cases) => set({ useCases: cases }),
      setEspChoice: (esp) => set({ espChoice: esp }),
      setKlaviyoConnected: (connected) => set({ klaviyoConnected: connected }),
      setAiGenerationStatus: (status) => set({ aiGenerationStatus: status }),
      setAiGenerationError: (error) => set({ aiGenerationError: error }),
      setGeneratedFormId: (id) => set({ generatedFormId: id }),
      setAuthData: (data) =>
        set({
          userId: data.userId,
          companyId: data.companyId,
          isAuthenticated: true,
        }),
      setInvitedUser: (isInvited, klaviyoConnected) =>
        set({
          isInvitedUser: isInvited,
          existingKlaviyoConnected: klaviyoConnected,
        }),
      reset: () => set(initialState),
    }),
    {
      name: 'kinetic-onboarding',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        currentStep: state.currentStep,
        completedSteps: state.completedSteps,
        firstName: state.firstName,
        lastName: state.lastName,
        email: state.email,
        userType: state.userType,
        brandName: state.brandName,
        shopUrl: state.shopUrl,
        useCases: state.useCases,
        espChoice: state.espChoice,
        isAuthenticated: state.isAuthenticated,
        userId: state.userId,
        companyId: state.companyId,
        isInvitedUser: state.isInvitedUser,
        generatedFormId: state.generatedFormId,
        aiGenerationStatus: state.aiGenerationStatus,
      }),
    }
  )
);
